import { forwardRef, type ReactNode } from 'react';

import { XIcon } from '@air/design-icons';
import { Button, type ButtonProps } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

import { ui2026FilterChip, ui2026FilterChipClose } from '~/components/Filters/FiltersBar/filtersBarUi2026';
import { type FilterIcon } from '~/components/FiltersMenu/types';

const MAX_VALUES_TO_SHOW = 3;

export type FilterButtonProps = {
  title: string | ReactNode;
  Icon?: FilterIcon;
  isOpen?: boolean;
  count?: number;
  values?: string[];
  onRemoveClick?: () => void;
} & Pick<ButtonProps, 'onClick'>;

export const FilterButton = forwardRef<HTMLButtonElement, FilterButtonProps>(
  ({ count, Icon, isOpen, onRemoveClick, title, values, ...restOfProps }: FilterButtonProps, ref) => {
    const hasCloseButton = !!onRemoveClick && (!!count || !!values?.length);

    const isActive = isOpen || !!values?.length || !!count;
    const isTruncated = (!!values && values.length > MAX_VALUES_TO_SHOW) || (!values && !!count);

    return (
      <div
        className="group relative shrink-0"
        data-testid="FILTER_BUTTON_CONTAINER"
        data-title={title}
        data-truncated={isTruncated}
      >
        <Button
          appearance="outline"
          className={cn(hasCloseButton ? 'pr-5' : 'pr-2', ui2026FilterChip({ isActive }))}
          color={isActive ? 'blue' : 'grey'}
          data-testid="FILTER_BUTTON"
          data-title={title}
          prefix={Icon ? <Icon className="shrink-0" color="currentColor" size="xs" /> : undefined}
          suffix={null}
          size="small"
          ref={ref}
          {...restOfProps}
        >
          <div className="flex items-center gap-1">
            {isTruncated && (count || values?.length) && (
              <span data-testid="FILTER_BUTTON_COUNT">{count || values?.length}</span>
            )}
            {values?.length && !isTruncated ? values.join(', ') : title}
          </div>
        </Button>
        {hasCloseButton && (
          <IconButton
            appearance="ghost"
            className={cn('absolute top-1/2 right-1 z-1 -translate-y-1/2', ui2026FilterChipClose())}
            color={isActive ? 'blue' : 'grey'}
            data-testid="FILTER_BUTTON_CLOSE"
            adsIcon
            icon={XIcon}
            label="Clear filters"
            onClick={onRemoveClick}
            size="extra-small"
          />
        )}
      </div>
    );
  },
);

FilterButton.displayName = 'FilterButton';
